import { useRef } from "react";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import Skeleton from "react-loading-skeleton";
import { toast } from "react-toastify";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation } from "swiper/modules";
import "swiper/css";
import { fetchRecipesByKitchen } from "../../../services/catalog.api";
import RecipeCard from "../../shared/RecipeCard";

const swiperBreakpoints = {
  1400: { slidesPerView: 5, spaceBetween: 14 },
  1200: { slidesPerView: 4, spaceBetween: 12 },
  992: { slidesPerView: 3, spaceBetween: 10 },
  576: { slidesPerView: 1.5, spaceBetween: 10 },
};

const arrowStyle = {
  position: "absolute",
  top: "50%",
  transform: "translateY(-50%)",
  width: 30,
  height: 30,
  borderRadius: "50%",
  border: "1px solid #e5e7eb",
  backgroundColor: "#ffffff",
  color: "#111827",
  boxShadow: "0 4px 10px rgba(15, 23, 42, 0.12)",
  zIndex: 20,
  cursor: "pointer",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  transition: "box-shadow 0.15s, background-color 0.15s",
  flexShrink: 0,
};

const fetchRecipeData = (kitchenId) => fetchRecipesByKitchen(kitchenId);

const KitchenRecipeParents = ({ kitchenId }) => {
  const swiperRef = useRef(null);

  const { data, isPending, error } = useQuery({
    queryKey: ["kitchenRecipeParents", kitchenId],
    queryFn: () => fetchRecipeData(kitchenId),
    enabled: !!kitchenId,
  });

  if (error) {
    toast.error(error?.message);
    return null;
  }

  if (data?.length === 0 && !isPending) return null;

  return (
    <div className="home-content-shell" style={{ paddingTop: 16, marginBottom: 24 }}>
      <div className="d-flex align-items-center justify-content-between" style={{ marginBottom: 12, paddingLeft: 4, paddingRight: 4 }}>
        <h5 style={{ fontWeight: 800, fontSize: 18, margin: 0, fontFamily: "'Inter', sans-serif", color: "#1a1a2e" }}>
          My Recipes
        </h5>
      </div>

      <div style={{ position: "relative" }}>
        <Swiper
          modules={[Navigation]}
          onSwiper={s => { swiperRef.current = s; }}
          slidesPerView={1.3}
          spaceBetween={12}
          loop={data?.length > 4}
          speed={500}
          breakpoints={swiperBreakpoints}
          style={{ paddingBottom: 8 }}
        >
          {isPending
            ? [1, 2, 3, 4, 5].map(i => (
                <SwiperSlide key={i}><Skeleton height={300} /></SwiperSlide>
              ))
            : data?.map(recipe => (
                <SwiperSlide key={recipe._id}><RecipeCard item={recipe} /></SwiperSlide>
              ))}
        </Swiper>
        <button
          onClick={() => swiperRef.current?.slidePrev()}
          style={{ ...arrowStyle, left: -6 }}
          aria-label="Previous slide"
          onMouseEnter={e => { e.currentTarget.style.backgroundColor = "#f9fafb"; e.currentTarget.style.boxShadow = "0 6px 16px rgba(15,23,42,0.18)"; }}
          onMouseLeave={e => { e.currentTarget.style.backgroundColor = "#ffffff"; e.currentTarget.style.boxShadow = "0 4px 10px rgba(15,23,42,0.12)"; }}
        >
          <ArrowLeft size={14} strokeWidth={2.2} />
        </button>
        <button
          onClick={() => swiperRef.current?.slideNext()}
          style={{ ...arrowStyle, right: -6 }}
          aria-label="Next slide"
          onMouseEnter={e => { e.currentTarget.style.backgroundColor = "#f9fafb"; e.currentTarget.style.boxShadow = "0 6px 16px rgba(15,23,42,0.18)"; }}
          onMouseLeave={e => { e.currentTarget.style.backgroundColor = "#ffffff"; e.currentTarget.style.boxShadow = "0 4px 10px rgba(15,23,42,0.12)"; }}
        >
          <ArrowRight size={14} strokeWidth={2.2} />
        </button>
      </div>
    </div>
  );
};

export default KitchenRecipeParents;
